/** API client for the OMS operator endpoints (spec §8.1).
 *  Backend router: src/topology_platform/api/v1/oms.py (prefix /oms).
 *
 *  Reads are viewer+, mutations are dispatcher; the server is the real gate.
 *  Mutations pass `expected_updated_at` for optimistic concurrency and surface a
 *  409 (ApiError.status === 409 — see isConflictError in client.ts) on a stale
 *  write. Every list endpoint is cursor-paged (opaque `next_cursor`). */

import { apiFetch, get, patch, post, ApiError } from './client.ts'
import type {
  AffectedMeterItem,
  CauseOption,
  ConvergentEventItem,
  CorrelatedEventItem,
  CriticalCustomerItem,
  OmsStatus,
  OutageCancel,
  OutageClose,
  OutageConfirm,
  OutageCreate,
  OutageDetail,
  OutageListItem,
  OutageMapFeatureCollection,
  OutageMerge,
  OutagePatch,
  OutageSplit,
  Page,
  PowertrxPreview,
  ProtectiveDeviceItem,
  ReliabilityMetricItem,
  RestorationLogItem,
  SourceDeviceConfirm,
  SourceDeviceMetrics,
  SourceDeviceOverride,
  UnmatchedNumberSummary,
  UnmatchedSignalItem,
} from '../types/oms.ts'

function buildQS(
  params: Record<string, string | number | boolean | undefined | null>,
): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

// =========================================================================
// Reads (viewer+)
// =========================================================================

/** Sort keys accepted by GET /oms/outages (mirrors _SORTABLE in the service). */
export type OutageSort =
  | 'severity'
  | 'start_time'
  | 'customers_affected'
  | 'status'
  | 'updated_at'

/** Active/filtered outage list — trust fields + critical suppression, paged. */
export function listOutages(params?: {
  status?: string
  feeder_id?: number
  scope?: string
  priority?: string
  cause_code?: string
  sort?: OutageSort
  limit?: number
  cursor?: string | null
  roots_only?: boolean
  parent_id?: number
}): Promise<Page<OutageListItem>> {
  return get<Page<OutageListItem>>(`/oms/outages${buildQS(params ?? {})}`)
}

/** Closed/cancelled events, filterable + paged. */
export function listOutageHistory(params?: {
  cause_code?: string
  feeder_id?: number
  limit?: number
  cursor?: string | null
}): Promise<Page<OutageListItem>> {
  return get<Page<OutageListItem>>(
    `/oms/outages/history${buildQS(params ?? {})}`,
  )
}

/** Outage detail (affected-meter list is a separate paged endpoint). */
export function getOutage(outageId: number): Promise<OutageDetail> {
  return get<OutageDetail>(`/oms/outages/${outageId}`)
}

/** Paged affected meters/addresses (PII; never an unbounded list, review #10). */
export function listAffectedMeters(
  outageId: number,
  params?: { q?: string; limit?: number; cursor?: string | null },
): Promise<Page<AffectedMeterItem>> {
  return get<Page<AffectedMeterItem>>(
    `/oms/outages/${outageId}/meters${buildQS(params ?? {})}`,
  )
}

/** PowerTRX's primary-cause tree (depth-first) for the cause picker. */
export function listCauses(): Promise<CauseOption[]> {
  return get<CauseOption[]>('/oms/causes')
}

/** What a PowerTRX export of [start, end] (local dates) would contain. */
export function getPowertrxPreview(params: {
  start: string
  end: string
  include_exported?: boolean
}): Promise<PowertrxPreview> {
  return get<PowertrxPreview>(`/oms/exports/powertrx${buildQS(params)}`)
}

/** Download PowerTRX's import CSV (oms_admin). The server marks the exported
 *  outages, so the next export leaves them out unless include_exported. */
export async function downloadPowertrxExport(
  body: {
    start: string
    end: string
    total_customers_served: number
    include_exported: boolean
  },
  filename: string,
): Promise<void> {
  const res = await apiFetch('/oms/exports/powertrx', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const detail = await res
      .json()
      .then(b => (typeof b.detail === 'string' ? b.detail : `HTTP ${res.status}`))
      .catch(() => `HTTP ${res.status}`)
    throw new ApiError(res.status, detail)
  }
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** Event timeline from oms_restoration_log (chronological). */
export function getOutageTimeline(
  outageId: number,
): Promise<RestorationLogItem[]> {
  return get<RestorationLogItem[]>(`/oms/outages/${outageId}/timeline`)
}

/** Critical-customer PHI detail — paged (phi_authorized; EVERY access audited). */
export function listCriticalCustomers(
  outageId: number,
  params?: { limit?: number; cursor?: string | null },
): Promise<Page<CriticalCustomerItem>> {
  return get<Page<CriticalCustomerItem>>(
    `/oms/outages/${outageId}/critical-customers${buildQS(params ?? {})}`,
  )
}

/** GeoJSON AGGREGATE features per outage (color-by-scope; NOT a meter list). */
export function getOutageMap(params?: {
  status?: string
}): Promise<OutageMapFeatureCollection> {
  return get<OutageMapFeatureCollection>(
    `/oms/map/outages${buildQS(params ?? {})}`,
  )
}

/** Pull heartbeat / data-as-of freshness for the banner (review #9). */
export function getOmsStatus(): Promise<OmsStatus> {
  return get<OmsStatus>('/oms/status')
}

/** Reliability metrics (SAIDI/SAIFI/CAIDI). Read-only; compute job is later. */
export function getReliabilityMetrics(params?: {
  period?: string
  scope?: string
  feeder_id?: number
  limit?: number
}): Promise<ReliabilityMetricItem[]> {
  return get<ReliabilityMetricItem[]>(
    `/oms/reliability/metrics${buildQS(params ?? {})}`,
  )
}

/** Unmatched-meter / unlinked report (oms_admin, §5.4). */
export function listUnmatchedSignals(params?: {
  limit?: number
}): Promise<UnmatchedSignalItem[]> {
  return get<UnmatchedSignalItem[]>(
    `/oms/ingest/unmatched${buildQS(params ?? {})}`,
  )
}

/** Distinct unmatched meter numbers with counts (oms_admin worklist). */
export function listUnmatchedSummary(params?: {
  limit?: number
}): Promise<UnmatchedNumberSummary[]> {
  return get<UnmatchedNumberSummary[]>(
    `/oms/ingest/unmatched/summary${buildQS(params ?? {})}`,
  )
}

/** Active protective devices on a feeder (the override picker). */
export function listFeederProtectiveDevices(
  feederId: number,
): Promise<ProtectiveDeviceItem[]> {
  return get<ProtectiveDeviceItem[]>(
    `/oms/feeders/${feederId}/protective-devices`,
  )
}

/** Other active events converging on this one's device chain (Merge & assign). */
export function getOutageConvergence(
  outageId: number,
): Promise<ConvergentEventItem[]> {
  return get<ConvergentEventItem[]>(`/oms/outages/${outageId}/convergence`)
}

/** Unparented meter events that started within the correlation window of this
 *  device event (or, for a meter event, its likely parent device event). */
export function getOutageCorrelated(
  outageId: number,
): Promise<CorrelatedEventItem[]> {
  return get<CorrelatedEventItem[]>(`/oms/outages/${outageId}/correlated`)
}

/** Suggestion-accuracy counts (confirmed vs overridden). */
export function getSourceDeviceMetrics(): Promise<SourceDeviceMetrics> {
  return get<SourceDeviceMetrics>('/oms/source-device/metrics')
}

// =========================================================================
// Mutations (dispatcher) — all carry expected_updated_at (409 on stale)
// =========================================================================

/** Manual outage create (dispatcher). Returns the created detail (201). */
export function createOutage(body: OutageCreate): Promise<OutageDetail> {
  return post<OutageDetail>('/oms/outages', body)
}

/** Update notes/cause/crew/priority — optimistic on updated_at (409 on stale). */
export function patchOutage(
  outageId: number,
  body: OutagePatch,
): Promise<OutageDetail> {
  return patch<OutageDetail>(`/oms/outages/${outageId}`, body)
}

/** Close an outage (dispatcher; optimistic on updated_at). */
export function closeOutage(
  outageId: number,
  body: OutageClose,
): Promise<OutageDetail> {
  return post<OutageDetail>(`/oms/outages/${outageId}/close`, body)
}

/** Cancel as a false positive (dispatcher; optimistic on updated_at). */
export function cancelOutage(
  outageId: number,
  body: OutageCancel,
): Promise<OutageDetail> {
  return post<OutageDetail>(`/oms/outages/${outageId}/cancel`, body)
}

/** Mark confirmed → reportable_for_reliability (dispatcher; optimistic). */
export function confirmOutage(
  outageId: number,
  body: OutageConfirm,
): Promise<OutageDetail> {
  return post<OutageDetail>(`/oms/outages/${outageId}/confirm`, body)
}

/** Merge/roll-up events under a primary (dispatcher; optimistic on primary). */
export function mergeOutages(body: OutageMerge): Promise<OutageDetail> {
  return post<OutageDetail>('/oms/outages/merge', body)
}

/** Split meters off an event into a new event (dispatcher; optimistic). */
export function splitOutage(
  outageId: number,
  body: OutageSplit,
): Promise<OutageDetail> {
  return post<OutageDetail>(`/oms/outages/${outageId}/split`, body)
}

/** Confirm the engine-suggested source device (dispatcher; optimistic). */
export function confirmSourceDevice(
  outageId: number,
  body: SourceDeviceConfirm,
): Promise<OutageDetail> {
  return post<OutageDetail>(`/oms/outages/${outageId}/source-device/confirm`, body)
}

/** Override the suggested source device with a picked device (dispatcher). */
export function overrideSourceDevice(
  outageId: number,
  body: SourceDeviceOverride,
): Promise<OutageDetail> {
  return post<OutageDetail>(`/oms/outages/${outageId}/source-device/override`, body)
}

// =========================================================================
// Report download (server-rendered CSV/Excel)
// =========================================================================

/** Download a server-rendered OMS report (CSV/Excel) with the auth token, then
 *  trigger a browser save. The export endpoints (history/reliability ?format=)
 *  are oms_admin-gated; a 403 surfaces as an ApiError the caller can show. */
export async function downloadOmsReport(
  path: string,
  filename: string,
): Promise<void> {
  const res = await apiFetch(path)
  if (!res.ok) {
    const detail = await res
      .json()
      .then(b => b.detail as string)
      .catch(() => `HTTP ${res.status}`)
    throw new ApiError(res.status, detail || `HTTP ${res.status}`)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
